import Button from '@navi/web-ui/lib/primitives/Button/Button';
import Chip from '@navi/web-ui/lib/primitives/Chip/index';
import cx from 'classnames';
import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { FieldErrorsImpl, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { RootState } from 'src/store';
import { createKey } from 'src/utils/CaseDetail.utils';
import { fetchSelectedQption, getQuestion, submitFormForCallBridge } from '../actions';
import RenderingEngine from '../component';
import styles from './../index.module.scss';
import {
  AnswerType,
  ECallingType,
  FEEDBACK_FORM_STATES,
  FeedbackFormKind,
  FeedbackPayload,
  IFeedbackFormSource,
  InteractionType,
  QuestionResponse
} from '../interfaces/index.type';
import {
  createPayloadForFeedbackHistory,
  feedbackFromReducer,
  getSortedOption,
  getStylesForChipInteractions,
  InteractionsEnum,
  mapQuestionToRadioOptions
} from '../utlis';
import Loader from '../../../../components/Loader/Loader';
import CancelCircleFilled from '../../../../assets/icons/CancelCircledFilled';
import { fetchFeedbackHistory } from '../../actions/overviewActions';
import { SESSION_STORAGE_KEYS } from '../../../../constants/StorageKeys';
import { fetchCallInteractions } from '../../actions/interactionsActions';
import {
  CALL_INTERACTIONS,
  getLastMonthDate,
  getTodayDate
} from '../../constants/communicationHistory.constant';
import { readQueryParams } from 'src/utils/QueryParamsHelper';
import { setBlankDisposition } from 'src/reducers/commonSlice';
import { NESTED_BLANK_DISPOSITION_QUERY } from '../../constants/Interactions.constant';
import { Countdown } from '../../../../components/Countdown/Countdown';
import Typography from '@primitives/Typography/Typography';
import { addClickstreamEvent } from '../../../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES, HRC_CLICKSTREAM } from '../../../../service/clickStream.constant';
import { clearInteraction, resetFeedbackQuestionTypes } from '../reducer/feedbackSlice';
import RadioGroup from '@navi/web-ui/lib/components/RadioGroup';
import { incrementDateByMins, utcToIST } from 'src/utils/DateHelper';
import { TextArea } from '@navi/web-ui/lib/primitives';
import { ExtensionHandler } from 'src/utils/extension.utils';
import { defaultFeedbackTypes } from '../../components/CommunicationHistory/constants';
import { submitChatFeedback } from '../../../WhatsappChatbot/actions';
import debounce from '@utils/Debounce';
import { toast } from '@primitives/Toast';
import CallDetailsAddFeedback from './CallDetailsAddFeedback';
import { CallType } from '@cp/src/components/Waveform/const';

interface AddCustomerFeedbackProps {
  referenceId: string;
  interactionType: InteractionType;
  onClose?: () => void;
  successCallback?: () => void;
  loanId: string;
  customerId: string;
  feedbackSubmitEnabled: boolean;
  commentRequired?: boolean;
  interactionId?: string;
  showDrawer?: boolean;
  connectedPhoneNumber?: string;
  callId?: string;
  interactionData?: any;
}
const interactionWarningTimeout = 15000;

const AddCustomerFeedback = (props: AddCustomerFeedbackProps) => {
  const {
    referenceId,
    interactionType,
    onClose,
    customerId,
    loanId,
    feedbackSubmitEnabled,
    successCallback,
    commentRequired = true,
    interactionId,
    showDrawer,
    connectedPhoneNumber,
    callId = '',
    interactionData
  } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { register, handleSubmit, reset, setValue, getValues, unregister, clearErrors } = useForm();
  const maxInputDate = incrementDateByMins(utcToIST(getTodayDate()), 1).toISOString().slice(0, 16);
  const defaultLastMonthDate = utcToIST(getLastMonthDate()).toISOString().slice(0, 16);
  const { isCallConnected, campaignId, reminderRefId } = useSelector(
    (state: RootState) => state.humanReminder
  );
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const currentChat = useSelector((state: RootState) => state.whatsappChatbot.currentChat);
  const hrcChatCustomerId = useSelector(
    (state: RootState) => state?.whatsappChatbot?.currentChat?.customerId
  );
  const { caseReferenceId = '', caseBusinessVertical = '' } = useParams();
  useEffect(() => {
    if (
      showDrawer ||
      interactionType === InteractionType.HUMAN_REMINDER ||
      interactionType === InteractionType.WHATSAPP_CHAT_HRC ||
      interactionType === InteractionType.WHATSAPP_TEXT
    ) {
      dispatch(
        getQuestion({ loanaccountnumber: loanId, customerreferenceid: customerId, interactionType })
      );
      dispatchFeedback({
        type: FeedbackFormKind.RESET_STATE
      });
      reset();
    }
  }, [showDrawer]);

  useEffect(() => {
    if (!isCallConnected) {
      let warningTimeout: NodeJS.Timeout;
      if (interactionType === InteractionType.HUMAN_REMINDER) {
        warningTimeout = setTimeout(() => {
          setShowWarningCountdown(true);
        }, interactionWarningTimeout);
      }
      return () => {
        warningTimeout && clearTimeout(warningTimeout);
      };
    }
  }, [isCallConnected]);

  const createPayloadForCallInteractions = () => {
    const { [CALL_INTERACTIONS]: queryParams } = readQueryParams();
    return {
      page_no: queryParams?.page || 0,
      page_size: queryParams?.size || 10,
      types: feedbackFilters ? feedbackFilters : defaultFeedbackTypes,
      loan_account_number: loanId,
      from: queryParams?.fromTime || defaultLastMonthDate,
      to: queryParams?.toTime || maxInputDate,
      caseReferenceId: caseReferenceId,
      caseBusinessVertical: caseBusinessVertical
    };
  };

  const getCallInteractionsData = () => {
    const payload = createPayloadForCallInteractions();
    dispatch(fetchCallInteractions(loanId, customerId, payload, false));
  };

  const questions = useSelector((state: RootState) => state.feedBack.questionTypes);
  const telephones = useSelector(
    (state: RootState) => state.caseDetail.pageData?.[createKey(loanId, customerId)]?.telephonesv2
  );
  const user = useSelector((state: RootState) => state.common.userData);
  const feedbackFilters = useSelector((state: RootState) => state.common.feedbackFilters);
  // @TODO : move to useReducer
  const [feedback, dispatchFeedback] = useReducer(feedbackFromReducer, {
    selectedChip: null,
    questionMap: null,
    error: null,
    telephone: telephones?.data?.[0]?.number,
    comment: '',
    loading: false,
    initialOptionId: '',
    initialQuestionId: '',
    interactionId: sessionStorage.getItem(SESSION_STORAGE_KEYS.INTERACTION_ID) || null,
    feedbackFormState: FEEDBACK_FORM_STATES.NOT_SUBMITED
  });

  useEffect(() => {
    dispatchFeedback({
      type: FeedbackFormKind.UPDATE_TELEPHONES,
      payload: { telephone: telephones?.data?.[0]?.number }
    });
  }, [telephones]);

  useEffect(() => {
    if (!questions?.length) return;
    const preSelected = questions?.[0]?.options?.find(option => option.metadata?.isDefault);
    if (preSelected) {
      handleSelectedChip(preSelected.text, preSelected.referenceId);
    }
  }, [questions]);

  useEffect(() => {
    if (interactionType === InteractionType.WHATSAPP_TEXT) {
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_TELE_WA_CHAT_FEEDBACK_FORM_LOADED, {
        lan: loanId,
        customerReferenceId: customerId,
        agentReferenceId: user?.referenceId
      });
    }
  }, []);

  const handleSelectedChip = useCallback(
    (selectedChip: string, refId: string) => {
      dispatchFeedback({
        type: FeedbackFormKind.SET_SELECTED_CHIP,
        payload: { selectedChip, initialOptionId: refId }
      });
      fetchOption(refId);
    },
    [interactionType]
  );

  const sucessCallback = (questionResponse: Array<QuestionResponse>) => {
    if (!feedback.questionMap?.length) {
      return dispatchFeedback({
        type: FeedbackFormKind.UPDATE_QUESTION_MAP,
        payload: { questionResponse }
      });
    }
  };

  const failureCallback = () => {
    toggleLoading(false);
    toast('something went wrong', { type: 'error' });
  };

  const toggleLoading = (loading = false) => {
    dispatchFeedback({
      type: FeedbackFormKind.SET_LOADING,
      payload: { loading }
    });
  };

  const fetchOption = useCallback(
    (refId: string) => {
      toggleLoading(true);
      fetchSelectedQption(
        loanId,
        customerId,
        refId,
        sucessCallback,
        interactionType,
        failureCallback
      );
    },
    [feedback]
  );

  const fetchOptionSelect = useCallback(
    (refId: string, questionId: string) => {
      toggleLoading(true);
      fetchSelectedQption(
        loanId,
        customerId,
        refId,
        sucessCallback,
        interactionType,
        failureCallback,
        questionId
      );
    },
    [feedback.questionMap]
  );

  const removeSelectedChip = useCallback(() => {
    dispatchFeedback({ type: FeedbackFormKind.REMOVE_SELECTED_CHIP });
    reset();
  }, [reset]);

  const sortedOptions = useMemo(() => {
    const sorted = [...(questions?.[0]?.options || [])];
    dispatchFeedback({
      payload: { initialQuestionId: questions?.[0]?.referenceId },
      type: FeedbackFormKind.SET_INITIAL_QUESTION_ID
    });
    return getSortedOption(sorted);
  }, [feedback.selectedChip, questions]);

  const initialQuestions = useMemo(() => {
    return questions && questions[0]?.answerType === 'RADIO_OPTIONS' ? (
      <div key={interactionType}>
        <RadioGroup
          options={mapQuestionToRadioOptions(questions[0].options, true)}
          value={feedback.selectedChip ?? ''}
          name="radioOptions"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const option = questions[0].options.find(option => option.text === e.target.value)!;
            handleSelectedChip(option?.text, option?.referenceId);
            addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Feedback_CustomerFeedback, {
              selectedFeedback: option?.text
            });
          }}
        />
      </div>
    ) : questions && questions[0]?.answerType === AnswerType.GROUPED_OPTIONS ? (
      <>
        {questions[0]?.groupedOptions?.map((optionGroup, index) => {
          return (
            // eslint-disable-next-line react/jsx-key
            <div className={cx(!feedback.selectedChip && styles.optionGroup)}>
              {getSortedOption(optionGroup)?.map(option => {
                if (!feedback.selectedChip)
                  return (
                    <Chip
                      key={option.referenceId}
                      label={option.text}
                      wrapperClasses={cx([
                        styles.chip,
                        getStylesForChipInteractions(
                          option?.metadata?.interactionLevel,
                          InteractionsEnum.HOVER
                        )
                      ])}
                      labelClasses={styles.text}
                      onChipClick={() => {
                        handleSelectedChip(option.text, option.referenceId);
                        if (interactionType === InteractionType.HUMAN_REMINDER) {
                          addClickstreamEvent(HRC_CLICKSTREAM.HRC_FEEDBACK_SELECTED, {
                            loanAccountNumber: loanId,
                            phoneNumber: connectedPhoneNumber,
                            customerRefId: customerId,
                            feedback: option.text,
                            campaignId,
                            agentRefrenceId: referenceId,
                            reminderRefId: reminderRefId,
                            ameyoStatus: ExtensionHandler.currentAmeyoStatus()
                          });
                        } else {
                          addClickstreamEvent(
                            CLICKSTREAM_EVENT_NAMES.LH_Feedback_CustomerFeedback,
                            {
                              selectedFeedback: option.text
                            }
                          );
                        }
                      }}
                    />
                  );
                if (option.text !== feedback.selectedChip) return null;
                return (
                  <Chip
                    key={option.referenceId}
                    label={option.text}
                    wrapperClasses={cx([
                      styles.chip,
                      getStylesForChipInteractions(
                        option?.metadata?.interactionLevel,
                        InteractionsEnum.ACTIVE_CHIP
                      )
                    ])}
                    RightAdornment={
                      <CancelCircleFilled
                        fillColor={getStylesForChipInteractions(
                          option?.metadata?.interactionLevel,
                          InteractionsEnum.ACTIVE_CANCEL_ICON
                        )}
                      />
                    }
                    labelClasses={styles.text}
                    onChipClick={removeSelectedChip}
                  />
                );
              })}
            </div>
          );
        })}
      </>
    ) : (
      <>
        <Typography variant={'p4'} as={'p'}>
          {questions?.[0]?.text}
        </Typography>
        <div className={styles.optionGroup}>
          {sortedOptions?.map(option => {
            if (!feedback.selectedChip)
              return (
                <Chip
                  key={option.referenceId}
                  label={option.text}
                  wrapperClasses={cx([
                    styles.chip,
                    getStylesForChipInteractions(
                      option?.metadata?.interactionLevel,
                      InteractionsEnum.HOVER
                    )
                  ])}
                  labelClasses={styles.text}
                  onChipClick={() => {
                    handleSelectedChip(option.text, option.referenceId);
                    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Feedback_CustomerFeedback, {
                      selectedFeedback: option.text
                    });
                  }}
                />
              );
            if (option.text !== feedback.selectedChip) return null;
            return (
              <Chip
                key={option.referenceId}
                label={option.text}
                wrapperClasses={cx([
                  styles.chip,
                  {
                    [getStylesForChipInteractions(
                      option?.metadata?.interactionLevel,
                      InteractionsEnum.ACTIVE_CHIP
                    )]: option.text === feedback.selectedChip
                  }
                ])}
                RightAdornment={
                  <CancelCircleFilled
                    fillColor={getStylesForChipInteractions(
                      option?.metadata?.interactionLevel,
                      InteractionsEnum.ACTIVE_CANCEL_ICON
                    )}
                  />
                }
                labelClasses={styles.text}
                onChipClick={removeSelectedChip}
              />
            );
          })}
        </div>
      </>
    );
  }, [sortedOptions, loanId, customerId, feedback]);

  const handleSubmitForm = useCallback(
    (formData: any) => {
      setIsFormSubmitting(true);
      dispatch({ type: FeedbackFormKind.BEFORE_API_CALL });
      let key: keyof typeof formData;
      const feedbacks = [];
      feedbacks.push({
        questionReferenceId: feedback.initialQuestionId,
        optionReferenceId: feedback.initialOptionId
      });
      for (key in formData) {
        const questionReferenceId = key.replace(
          `${IFeedbackFormSource.ADD_INTERACTION_BUTTON}_`,
          ''
        );
        if (questionReferenceId.includes('_option') && formData[key]) {
          feedbacks.push({
            questionReferenceId: questionReferenceId.replace('_option', ''),
            optionReferenceId: formData[key]
          });
        } else if (formData[key]) {
          feedbacks.push({
            questionReferenceId: questionReferenceId,
            answerText: formData[key]
          });
        }
      }
      const selectedTelephone = telephones?.data
        ?.map(telephone => ({
          number: telephone.number,
          source: telephone.source,
          numberCustomId: telephone.numberCustomId
        }))
        ?.find(telephone => telephone.number === feedback.telephone);
      let payload: FeedbackPayload = {
        feedbacks,
        accountNumber: loanId,
        agentPhoneNumber: user?.phoneNumber || '',
        interactionReminderDTO: null,
        recipientName: user?.name || '',
        recipientPhoneNumber: selectedTelephone?.number || '',
        recipientPhoneNumberCustomId: selectedTelephone?.numberCustomId || '',
        sourceType: selectedTelephone?.source ? `${selectedTelephone.source}_PHONE_CALL` : null,
        comments: feedback.comment || '',
        agentReferenceId: user?.referenceId,
        referenceId
      };
      if (callId) {
        payload = {
          callId: callId,
          feedbacks
        };
      }
      let headers: Record<string, string> = {};
      if (interactionType === InteractionType.HUMAN_REMINDER) {
        headers = { 'X-HR-REF-ID': referenceId };
      }
      if (interactionType === InteractionType.HUMAN_REMINDER) {
        addClickstreamEvent(HRC_CLICKSTREAM.HRC_SUBMIT_FEEDBACK_CLICK, {
          feedback: feedbacks,
          feedbackText: feedback.selectedChip,
          loanAccountNumber: loanId,
          customerId,
          reminderRefId,
          campaignId,
          connectedPhoneNumber,
          disconnected: !isCallConnected,
          agentRefrenceId: referenceId,
          ameyoStatus: ExtensionHandler.currentAmeyoStatus()
        });
      } else {
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Interactions_AddFeedbackSubmit, {
          feedback: feedbacks
        });
      }

      if (
        interactionType === InteractionType.WHATSAPP_CHAT_HRC ||
        interactionType === InteractionType.WHATSAPP_TEXT
      ) {
        toggleLoading(true);
        delete payload.referenceId;
        payload.agentReferenceId = user?.referenceId;
        payload.interactionMetadata = {
          chatReferenceId: referenceId
        };
        payload.sourceType = 'PRIMARY_PHONE_CALL';
        payload.recipientPhoneNumber = currentChat?.phoneNumber || '';
        payload.interactionType =
          interactionType === InteractionType.WHATSAPP_CHAT_HRC
            ? ECallingType.WHATSAPP_CHAT_HRC
            : ECallingType.WHATSAPP_TEXT;
        const successCallback = () => {
          setIsFormSubmitting(false);
          dispatchFeedback({
            type: FeedbackFormKind.RESET_STATE
          });
          toggleLoading();
        };
        const failureCallback = () => {
          setIsFormSubmitting(false);
          toggleLoading();
        };
        submitChatFeedback(
          loanId,
          hrcChatCustomerId || '',
          payload,
          successCallback,
          failureCallback
        );
      } else {
        submitFormForCallBridge(
          loanId,
          customerId,
          payload,
          feedbackSubmittedSuccessfully,
          feedbackSubmissionFailed,
          interactionType === InteractionType.HUMAN_REMINDER
            ? interactionId + ''
            : referenceId + '',
          headers,
          callId
        );
      }

      onClose && onClose();
    },
    [user, feedback, loanId]
  );

  const feedbackSubmittedSuccessfully = useCallback(
    (feedbacks: any) => {
      setIsFormSubmitting(false);
      if (interactionType === InteractionType.HUMAN_REMINDER) {
        addClickstreamEvent(HRC_CLICKSTREAM.HRC_SUBMIT_FEEDBACK_SUCCESS, {
          loanAccountNumber: loanId,
          reminderRefId: reminderRefId,
          ameyoStatus: ExtensionHandler.currentAmeyoStatus(),
          customerId,
          campaignId,
          disconnected: !isCallConnected,
          connectedPhoneNumber,
          feedback: feedbacks,
          feedbackText: feedback.selectedChip,
          agentRefrenceId: referenceId
        });
      }

      if (successCallback) {
        successCallback();
      }
      dispatch(setBlankDisposition({}));
      dispatch(clearInteraction({}));
      dispatch(resetFeedbackQuestionTypes());
      // PubSub.publish(TOPICS.CLEAR_INTERACTION_ID, {});
      removeQueryParams(NESTED_BLANK_DISPOSITION_QUERY);
      dispatchFeedback({ type: FeedbackFormKind.SUCESSFUL_API_CALL });
      const payload = createPayloadForFeedbackHistory(
        loanId,
        caseReferenceId,
        caseBusinessVertical
      );
      reset();
      // TODO : Temp fix - use source when it is merged with feedback-form-fix-v2
      if (interactionType !== InteractionType.HUMAN_REMINDER) {
        dispatch(fetchFeedbackHistory(loanId, customerId, payload));
        getCallInteractionsData();
      }
    },
    [reset]
  );

  const feedbackSubmissionFailed = useCallback((feedbacks: any, error: any) => {
    setIsFormSubmitting(false);
    if (interactionType === InteractionType.HUMAN_REMINDER) {
      addClickstreamEvent(HRC_CLICKSTREAM.HRC_SUBMIT_FEEDBACK_FAILURE, {
        reminderRefId: reminderRefId,
        loanAccountNumber: loanId,
        customerId,
        campaignId,
        disconnected: !isCallConnected,
        connectedPhoneNumber,
        feedback: feedbacks,
        feedbackText: feedback.selectedChip,
        agentRefrenceId: referenceId,
        ameyoStatus: ExtensionHandler.currentAmeyoStatus(),
        error
      });
    }

    dispatchFeedback({ type: FeedbackFormKind.BEFORE_API_CALL });
  }, []);

  const handleCommentChange = useCallback((changeEvent: React.ChangeEvent<HTMLInputElement>) => {
    dispatchFeedback({
      type: FeedbackFormKind.UPDATE_COMMENT,
      payload: { comment: changeEvent.target.value }
    });
  }, []);

  const handleFeedbackFormError = useCallback(
    (
      error: Partial<
        FieldErrorsImpl<{
          [x: string]: any;
        }>
      >
    ) => {
      dispatchFeedback({ type: FeedbackFormKind.HANDLE_FORM_ERROR, payload: { error } });
    },
    []
  );

  const removeQueryParams = (query: string) => {
    const param = searchParams.get(query);
    if (param) {
      searchParams.delete(query);
      setSearchParams(searchParams);
    }
  };

  const disableSubmitButton =
    (interactionType === InteractionType.HUMAN_REMINDER ||
    interactionType === InteractionType.WHATSAPP_CHAT_HRC ||
    interactionType === InteractionType.WHATSAPP_TEXT
      ? feedback.loading || !feedback.questionMap || !feedbackSubmitEnabled
      : !feedback.questionMap || !feedback.telephone || feedback.loading) || isFormSubmitting;

  const [showWarningCountdown, setShowWarningCountdown] = useState<boolean>(false);
  const [showLateCounter, setShowLateCounter] = useState<boolean>(false);
  const [showRedBackground, setShowRedBackground] = useState<boolean>(false);
  const onWarningCountdownComplete = () => {
    setShowWarningCountdown(false);
    setShowLateCounter(true);
  };
  const lateCounterStep = (step: number) => {
    setShowRedBackground(step % 2 === 1);
  };
  const header = `Add ${
    [InteractionType.WHATSAPP_CHAT_HRC, InteractionType.WHATSAPP_TEXT].includes(interactionType)
      ? 'chat'
      : 'call'
  } feedback`;

  const debouncedSubmit = debounce(handleSubmit(handleSubmitForm, handleFeedbackFormError), 1000);

  return (
    <div
      key={`${feedback}-${referenceId}-${IFeedbackFormSource}`}
      className={cx(styles.container, showRedBackground && styles.redBackground, 'pt-1')}
    >
      <>
        <div className={cx(styles.feedback)}>
          <>
            {interactionData?.type === CallType.SELF_CALL ? (
              <CallDetailsAddFeedback interactionData={interactionData} />
            ) : null}

            <div className={'mb-1'}>
              <Typography variant="h4">{header}</Typography>
            </div>
            <div className={styles.questions}>{initialQuestions}</div>
            <form
              onSubmit={handleSubmit(handleSubmitForm, handleFeedbackFormError)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault(); // Prevent form submission on Enter
                }
              }}
            >
              {feedback?.questionMap?.map(question =>
                question ? (
                  <RenderingEngine
                    fetchOption={fetchOptionSelect}
                    register={register}
                    question={question}
                    key={`${IFeedbackFormSource.ADD_INTERACTION_BUTTON}_${question.referenceId}`}
                    error={feedback.error}
                    toggleLoading={toggleLoading}
                    interactionType={interactionType}
                    setValue={setValue}
                    getValue={getValues}
                    lan={loanId}
                    customerId={customerId}
                    source={IFeedbackFormSource.ADD_INTERACTION_BUTTON}
                    unregister={unregister}
                    clearErrors={clearErrors}
                  />
                ) : null
              )}
              {feedback?.selectedChip && commentRequired && (
                <TextArea
                  rows={4}
                  resize
                  placeholder="Type here..."
                  inputSize="full-width"
                  value={feedback.comment}
                  onChange={handleCommentChange}
                  containerClassName={styles.addFeedbackTextArea}
                  id="comments"
                  label="Comments (if any)"
                />
              )}
            </form>
          </>
        </div>
        {showWarningCountdown ? (
          <div className={styles.countdownContainer}>
            <Typography className={styles.greyText} variant="p3">
              {`Warning : `}
              <span className={styles.redText}>
                <Countdown startFrom={15} decrementBy={1} onComplete={onWarningCountdownComplete} />
                {` seconds`}
              </span>
              {` left`}
            </Typography>
          </div>
        ) : (
          <></>
        )}
        {showLateCounter ? (
          <div className={styles.countdownContainer}>
            <Typography className={styles.greyText} variant="p3">
              {`You are late by : `}
              <span className={styles.redText}>
                <Countdown startFrom={0} isReverseCounter decrementBy={-1} step={lateCounterStep} />
                {` seconds`}
              </span>
            </Typography>
          </div>
        ) : (
          <></>
        )}
        <div className={cx(showRedBackground && styles.redBackground, styles.submitButtonSection)}>
          <Button
            disabled={disableSubmitButton}
            fullWidth
            type={'submit'}
            onClick={debouncedSubmit}
          >
            Submit Feedback
          </Button>
        </div>
        <Loader show={feedback.loading || false} className={styles.loadingState} animate={false} />
      </>
    </div>
  );
};

export default React.memo(AddCustomerFeedback);
